/**
 * ประวัติการจ่ายเงินเดือนย้อนหลัง — ลิสต์ทุกรอบ → กางดูตารางพนักงาน →
 * พิมพ์ (PDF) + ดูสลิปรายคน. อ่านอย่างเดียวจาก snapshot ที่ล็อกไว้ (payroll_runs).
 */
import { useState } from 'react';
import { Banknote, Coins, ChevronDown, Printer, FileText, Users } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Modal } from '../../components/ui/Modal';
import { usePayrollHistory, usePayrollRunRows, usePayrollRunDetail, usePayoutRounds, usePayoutRound, type PayrollHistoryRow, type PayrollRunRow, type PayoutRoundSummary } from '../../hooks';
import { empName } from '../../lib/emp-name';
import { apiFetch } from '../../lib/api-client';
import { useToast } from '../../components/ui/toast';

const STATUS: Record<string, { label: string; cls: string }> = {
  draft: { label: 'ร่าง', cls: 'bg-gray-100 text-warm-gray' },
  previewed: { label: 'ตรวจแล้ว', cls: 'bg-gold-wash text-gold-deep' },
  approved: { label: 'อนุมัติแล้ว', cls: 'bg-gold-wash text-gold-deep' },
  locked: { label: 'ปิดงวดแล้ว', cls: 'bg-[#e7f4ec] text-[#2f7d52]' },
  pdf_ready: { label: 'ปิดงวดแล้ว', cls: 'bg-[#e7f4ec] text-[#2f7d52]' },
  sending: { label: 'กำลังส่งสลิป', cls: 'bg-[#e7f4ec] text-[#2f7d52]' },
  partial_sent: { label: 'ส่งสลิปบางส่วน', cls: 'bg-[#e7f4ec] text-[#2f7d52]' },
  sent: { label: 'จ่าย & ส่งสลิปแล้ว', cls: 'bg-[#e7f4ec] text-[#2f7d52]' },
};
const MTH = ['', 'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];
function fmtPeriod(p: string): string {
  const [y, m] = p.split('-').map(Number);
  if (!y || !m) return p;
  return `${MTH[m]} ${y + 543}`;
}
const baht = (n: number) => '฿' + (Number(n) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const esc = (s: string) => String(s).replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] ?? c));

function printRun(period: string, rows: PayrollRunRow[]) {
  const today = new Date().toLocaleDateString('th-TH');
  const tr = rows.map((r, i) => `<tr><td class="c">${i + 1}</td><td>${esc(empName(r, r.name || r.emp_code))}<div class="m">${esc(r.emp_code)}</div></td>
    <td class="r">${(Number(r.gross) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</td>
    <td class="r">${(Number(r.tax_amount) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</td>
    <td class="r">${(Number(r.sso_amount) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</td>
    <td class="r b">${(Number(r.net) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</td></tr>`).join('');
  const sum = (k: keyof PayrollRunRow) => rows.reduce((a, r) => a + (Number(r[k]) || 0), 0).toLocaleString('th-TH', { minimumFractionDigits: 2 });
  const html = `<!DOCTYPE html><html lang="th"><head><meta charset="utf-8"><title>สรุปการจ่ายเงินเดือน ${esc(period)}</title>
  <style>*{font-family:'Sarabun','Tahoma',sans-serif;box-sizing:border-box}body{margin:24px;color:#2a2226;font-size:13px}
  h1{font-size:18px;margin:0 0 2px}.meta{color:#6b6663;font-size:12px;margin-bottom:14px}
  table{width:100%;border-collapse:collapse}th,td{padding:6px 8px;border-bottom:1px solid #e8dde1;text-align:left}
  th{background:#fbeaf1;font-weight:600;font-size:12px}.r{text-align:right}.c{text-align:center}.b{font-weight:700}.m{color:#9a938c;font-size:10px}
  tfoot td{border-top:2px solid #9a938c;font-weight:700;background:#F9F5F1}@media print{body{margin:0}}</style></head><body>
  <h1>สรุปการจ่ายเงินเดือน — งวด ${esc(fmtPeriod(period))}</h1>
  <div class="meta">พนักงาน ${rows.length} คน · พิมพ์เมื่อ ${esc(today)}</div>
  <table><thead><tr><th class="c">#</th><th>พนักงาน</th><th class="r">รายได้รวม</th><th class="r">ภาษี</th><th class="r">ปกส.</th><th class="r">สุทธิ</th></tr></thead>
  <tbody>${tr}</tbody>
  <tfoot><tr><td></td><td>รวม</td><td class="r">${sum('gross')}</td><td class="r">${sum('tax_amount')}</td><td class="r">${sum('sso_amount')}</td><td class="r">${sum('net')}</td></tr></tfoot>
  </table></body></html>`;
  const w = window.open('', '_blank');
  if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 400); }
}

function SlipModal({ period, emp, name, onClose }: { period: string; emp: string | null; name: string; onClose: () => void }) {
  const { data, isLoading } = usePayrollRunDetail(emp ? period : null, emp);
  return (
    <Modal open={!!emp} onClose={onClose} title={`สลิปเงินเดือน · ${name}`} size="max-w-md">
      {isLoading && <p className="text-sm text-muted">กำลังโหลด…</p>}
      {data && (
        <div className="space-y-3 text-sm">
          <p className="text-xs text-warm-gray">งวด {fmtPeriod(period)} · {emp}</p>
          <div>
            <p className="font-display font-semibold text-ink mb-1">รายได้</p>
            {(data.earnings ?? []).map((e, i) => (
              <div key={i} className="flex justify-between py-0.5"><span className="text-warm-gray">{e.label}</span><span className="num">{baht(e.amount)}</span></div>
            ))}
            <div className="flex justify-between border-t border-[color:rgba(13,11,10,.08)] mt-1 pt-1"><b>รวมรายได้</b><b className="num">{baht(data.earnings_total)}</b></div>
          </div>
          <div>
            <p className="font-display font-semibold text-ink mb-1">รายการหัก</p>
            {(data.deductions ?? []).map((e, i) => (
              <div key={i} className="flex justify-between py-0.5"><span className="text-warm-gray">{e.label}</span><span className="num text-[#b4564f]">−{baht(e.amount)}</span></div>
            ))}
            <div className="flex justify-between border-t border-[color:rgba(13,11,10,.08)] mt-1 pt-1"><b>รวมหัก</b><b className="num text-[#b4564f]">−{baht(data.deductions_line_total)}</b></div>
          </div>
          <div className="flex justify-between items-center bg-gold-wash rounded-lg px-3 py-2">
            <b className="text-ink">รับสุทธิ</b><b className="num text-lg text-gold-deep">{baht(data.net)}</b>
          </div>
        </div>
      )}
    </Modal>
  );
}

function RunCard({ run }: { run: PayrollHistoryRow }) {
  const [open, setOpen] = useState(false);
  const [slipEmp, setSlipEmp] = useState<{ emp: string; name: string } | null>(null);
  const rows = usePayrollRunRows(open ? run.period : null);
  const st = STATUS[run.status] ?? { label: run.status, cls: 'bg-gray-100 text-warm-gray' };

  return (
    <div className="card overflow-hidden">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-3 p-4 text-left">
        <span className="w-10 h-10 rounded-xl bg-gold-wash text-gold-deep grid place-items-center shrink-0"><Banknote className="w-5 h-5" /></span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-display font-semibold text-ink">งวด {fmtPeriod(run.period)}</p>
            <span className={`text-[10px] rounded-full px-2 py-0.5 font-medium ${st.cls}`}>{st.label}</span>
          </div>
          <p className="text-xs text-warm-gray mt-0.5 flex items-center gap-1"><Users className="w-3 h-3" /> {run.employee_count} คน · จ่ายสุทธิ <span className="num">{baht(run.total_net)}</span></p>
        </div>
        <ChevronDown className={`w-4 h-4 text-muted shrink-0 transition ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="border-t border-[color:rgba(13,11,10,.08)]">
          <div className="flex justify-end gap-2 px-4 py-2.5">
            <button onClick={() => rows.data && printRun(run.period, rows.data)} disabled={!rows.data?.length} className="btn-secondary btn-slim inline-flex items-center gap-1"><Printer className="w-3.5 h-3.5" /> พิมพ์</button>
          </div>
          {rows.isLoading && <p className="text-sm text-muted px-4 pb-4">กำลังโหลด…</p>}
          {rows.data && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-[11px] uppercase tracking-wider text-muted bg-[#faf6ee]">
                  <th className="text-left font-medium px-4 py-2">พนักงาน</th>
                  <th className="text-right font-medium px-2 py-2">รายได้รวม</th>
                  <th className="text-right font-medium px-2 py-2">ภาษี</th>
                  <th className="text-right font-medium px-2 py-2">สุทธิ</th>
                  <th className="px-3 py-2"></th>
                </tr></thead>
                <tbody>
                  {rows.data.map((r) => (
                    <tr key={r.id} onClick={() => setSlipEmp({ emp: r.emp_code, name: empName(r, r.name || r.emp_code) })} className="border-t border-[color:rgba(13,11,10,.06)] cursor-pointer hover:bg-[#faf6ee]">
                      <td className="px-4 py-2"><div className="text-ink font-medium">{empName(r, r.name || r.emp_code)}</div><div className="num text-[11px] text-muted">{r.emp_code}</div></td>
                      <td className="px-2 py-2 text-right num">{baht(r.gross)}</td>
                      <td className="px-2 py-2 text-right num text-warm-gray">{baht(r.tax_amount)}</td>
                      <td className="px-2 py-2 text-right num font-semibold">{baht(r.net)}</td>
                      <td className="px-3 py-2 text-right"><span className="text-gold-deep inline-flex items-center gap-1 text-[13px]"><FileText className="w-3.5 h-3.5" /> ดูสลิป</span></td>
                    </tr>
                  ))}
                  {rows.data.length === 0 && <tr><td colSpan={5} className="text-center text-muted py-4">ไม่มีข้อมูลในงวดนี้</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      <SlipModal period={run.period} emp={slipEmp?.emp ?? null} name={slipEmp?.name ?? ''} onClose={() => setSlipEmp(null)} />
    </div>
  );
}

/** การ์ดรอบ "จ่ายนอกรอบ/ค่าคอม" (payout ledger) — แสดงรวมในไทม์ไลน์เดียวกับเงินเดือนระบบ.
 *  คลิกที่ชื่อพนักงาน = เปิดสลิป PDF (เฉพาะรอบที่จ่ายแล้ว). */
function PayoutCard({ round }: { round: PayoutRoundSummary }) {
  const [open, setOpen] = useState(false);
  const detail = usePayoutRound(open ? round.id : null);
  const toast = useToast();
  const [slipLoading, setSlipLoading] = useState<string | null>(null);
  const [pdf, setPdf] = useState<{ url: string; name: string } | null>(null);
  const isPaid = round.status === 'paid';
  const st = isPaid
    ? { label: 'จ่ายแล้ว', cls: 'bg-[#e7f4ec] text-[#2f7d52]' }
    : { label: 'ร่าง', cls: 'bg-gray-100 text-warm-gray' };

  async function openSlip(emp: string, name: string) {
    if (!isPaid) { toast.error('สลิปดูได้เฉพาะรอบที่ยืนยันจ่ายแล้ว'); return; }
    if (slipLoading) return;
    setSlipLoading(emp);
    try {
      const { pdf_url } = await apiFetch<{ pdf_url: string }>(`/hr/payouts/rounds/${round.id}/slip-pdf?emp=${encodeURIComponent(emp)}`);
      setPdf({ url: pdf_url, name });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'สร้างสลิปไม่สำเร็จ');
    } finally { setSlipLoading(null); }
  }

  return (
    <div className="card overflow-hidden">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-3 p-4 text-left">
        <span className="w-10 h-10 rounded-xl bg-gold-wash text-gold-deep grid place-items-center shrink-0"><Coins className="w-5 h-5" /></span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-display font-semibold text-ink">{round.title}</p>
            <span className={`text-[10px] rounded-full px-2 py-0.5 font-medium ${st.cls}`}>{st.label}</span>
          </div>
          <p className="text-xs text-warm-gray mt-0.5 flex items-center gap-1"><Users className="w-3 h-3" /> {round.totals.people} คน · จ่ายสุทธิ <span className="num">{baht(round.totals.net)}</span></p>
        </div>
        <ChevronDown className={`w-4 h-4 text-muted shrink-0 transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="border-t border-[color:rgba(13,11,10,.08)]">
          {detail.isLoading && <p className="text-sm text-muted px-4 py-4">กำลังโหลด…</p>}
          {detail.data && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-[11px] uppercase tracking-wider text-muted bg-[#faf6ee]">
                  <th className="text-left font-medium px-4 py-2">พนักงาน</th>
                  <th className="text-right font-medium px-2 py-2">รายได้รวม</th>
                  <th className="text-right font-medium px-2 py-2">หัก ณ ที่จ่าย</th>
                  <th className="text-right font-medium px-2 py-2">สุทธิ</th>
                  <th className="px-3 py-2"></th>
                </tr></thead>
                <tbody>
                  {detail.data.items.map((it) => (
                    <tr key={it.id} onClick={() => void openSlip(it.emp_code, empName(it.employee, it.emp_code))} className={`border-t border-[color:rgba(13,11,10,.06)] ${isPaid ? 'cursor-pointer hover:bg-[#faf6ee]' : ''} ${slipLoading === it.emp_code ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-2"><div className="text-ink font-medium">{empName(it.employee, it.emp_code)}</div><div className="num text-[11px] text-muted">{it.emp_code}</div></td>
                      <td className="px-2 py-2 text-right num">{baht(it.gross)}</td>
                      <td className="px-2 py-2 text-right num text-warm-gray">{baht(it.wht_amount)}</td>
                      <td className="px-2 py-2 text-right num font-semibold">{baht(it.net)}</td>
                      <td className="px-3 py-2 text-right">{isPaid && <span className="text-gold-deep inline-flex items-center gap-1 text-[13px]"><FileText className="w-3.5 h-3.5" /> ดูสลิป</span>}</td>
                    </tr>
                  ))}
                  {detail.data.items.length === 0 && <tr><td colSpan={5} className="text-center text-muted py-4">ไม่มีข้อมูลในรอบนี้</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      <Modal open={!!pdf} onClose={() => setPdf(null)} title={pdf ? `สลิป · ${pdf.name}` : 'สลิป'} size="max-w-3xl">
        {pdf && <iframe src={pdf.url} title="สลิป" className="w-full h-[78vh] rounded-lg border border-[color:rgba(13,11,10,.08)]" />}
      </Modal>
    </div>
  );
}

export function OwnerPayrollHistory() {
  const { data, isLoading } = usePayrollHistory();
  const year = new Date().getFullYear();
  const payouts = usePayoutRounds(year);
  const runs = data?.items ?? [];
  const payoutRounds = (payouts.data ?? []).filter((r) => r.status !== 'cancelled' && r.totals.people > 0);

  // รวมไทม์ไลน์เดียว: เงินเดือนระบบ (payroll_runs) + จ่ายนอกรอบ/ค่าคอม (payout) เรียงใหม่→เก่า
  const cards = [
    ...runs.map((r) => ({ key: `run-${r.period}`, date: `${r.period}-15`, node: <RunCard key={`run-${r.period}`} run={r} /> })),
    ...payoutRounds.map((r) => ({ key: `po-${r.id}`, date: r.pay_date, node: <PayoutCard key={`po-${r.id}`} round={r} /> })),
  ].sort((a, b) => b.date.localeCompare(a.date));

  const loading = isLoading || payouts.isLoading;
  return (
    <AdminLayout title="ประวัติการจ่ายเงินเดือน">
      <div className="max-w-2xl mx-auto space-y-3 pb-10">
        <p className="text-sm text-warm-gray">รอบเงินเดือน + ค่าคอม/จ่ายนอกรอบ ทั้งหมด — กดเปิดรอบ แล้วคลิกชื่อพนักงานเพื่อดูสลิป</p>
        {loading && <div className="card p-6 text-center text-muted">กำลังโหลด…</div>}
        {!loading && cards.length === 0 && <div className="card p-6 text-center text-muted">ยังไม่มีประวัติการจ่าย</div>}
        {cards.map((c) => c.node)}
      </div>
    </AdminLayout>
  );
}
